import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  FiMessageSquare,
  FiPenTool,
  FiRefreshCw,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiPhone,
  FiMessageCircle,
  FiUser,
  FiZap,
} from 'react-icons/fi';
import './DayPlan.css';
import './modals/ConsultationInvitation.css';

const baseURL = '';

    

function getType(appointment) {
  if (appointment.day_type === 'consultation') return 'consultation';

  const text = `${appointment.description || ''}`.toLowerCase();

  if (text.includes('nachstechen') || text.includes('touchup')) {
    return 'touchup';
  }

  return 'tattoo';
}

function getLabel(type) {
  if (type === 'consultation') return 'Beratung';
  if (type === 'touchup') return 'Nachstechen';
  return 'Stechtermin';
}

function formatTime(time) {
  if (!time) return '--:--';
  return moment(time, ['HH:mm:ss', 'HH:mm']).format('HH:mm');
}

function getTypeIcon(type) {
  if (type === 'consultation') return <FiMessageSquare />;
  if (type === 'touchup') return <FiRefreshCw />;
  return <FiPenTool />;
}

function getWorkflowIcon(status) {
  if (status === 'arrived') return <FiUser />;
  if (status === 'in_progress') return <FiZap />;
  if (status === 'done') return <FiCheckCircle />;
  return <FiClock />;
}

function cleanPhone(phone) {
  if (!phone) return null;
  return phone.replace(/\D/g, '').replace(/^0/, '49');
}

function DayPlan({ selectedLocationId }) {
  const [appointmentsToday, setAppointmentsToday] = useState([]);
  const [loading, setLoading] = useState(true);

const [dayOffset, setDayOffset] = useState(0);

const selectedDay = moment().add(dayOffset, 'days').format('YYYY-MM-DD');

  const fetchDayPlan = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${baseURL}/api/dayplan`, {
        params: {
          locationId: selectedLocationId,
          date: selectedDay,
        },
      });

      const todayItems = res.data
        .map((appointment) => ({
          ...appointment,
          type: getType(appointment),
          workflow_status: appointment.workflow_status || 'waiting',
        }))
        .sort((a, b) =>
          moment(a.time, ['HH:mm:ss', 'HH:mm']).diff(
            moment(b.time, ['HH:mm:ss', 'HH:mm'])
          )
        );

      setAppointmentsToday(todayItems);
    } catch (err) {
      console.error('Error fetching day plan:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId, selectedDay]);

  useEffect(() => {
    if (selectedLocationId) fetchDayPlan();
  }, [selectedLocationId, selectedDay, fetchDayPlan]);


  

  const toggleWorkflowStatus = async (appointmentId) => {
    try {
      const res = await axios.put(
        `${baseURL}/api/appointments/${appointmentId}/workflow-status`
      );

      setAppointmentsToday((prev) =>
        prev.map((appointment) =>
          appointment.id === appointmentId
            ? { ...appointment, workflow_status: res.data.workflow_status }
            : appointment
        )
      );
    } catch (err) {
      console.error('Error toggling workflow status:', err);
    }
  };

  const stats = useMemo(() => {
    return {
      consultations: appointmentsToday.filter((a) => a.type === 'consultation').length,
      tattoo: appointmentsToday.filter((a) => a.type === 'tattoo').length,
      touchup: appointmentsToday.filter((a) => a.type === 'touchup').length,
      total: appointmentsToday.length,
    };
  }, [appointmentsToday]);

  const nextAppointment = useMemo(() => {
    const now = moment();

    return appointmentsToday.find((a) => {
      const appointmentTime = moment(
        `${selectedDay} ${a.time}`,
        ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'],
        true
      );

      return (!a.invitation_state || a.invitation_state === 'confirmed') && appointmentTime.isValid() && appointmentTime.isAfter(now);
    });
  }, [appointmentsToday, selectedDay]);

  const isPast = (appointment) => {
    const appointmentTime = moment(
      `${selectedDay} ${appointment.time}`,
      ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'],
      true
    );

    return appointmentTime.isValid() && appointmentTime.isBefore(moment());
  };

  const phoneNumber = cleanPhone(nextAppointment?.phone);

  return (
    <div className="dayplan-page">
     <header className="dayplan-header">
  <div>
    <h1>DayPlan</h1>
    <p>{moment(selectedDay).format('dddd, DD. MMMM YYYY')}</p>
  </div>

  <div className="dayplan-date-nav">
    <button onClick={() => setDayOffset(prev => prev - 1)}>←</button>
    <button onClick={() => setDayOffset(0)}>Heute</button>
    <button onClick={() => setDayOffset(prev => prev + 1)}>→</button>
  </div>
</header>

      <section className={`now-playing ${nextAppointment ? nextAppointment.type : 'empty'}`}>
        <div className="now-label">
          <FiClock />
          <span>Nächster Termin</span>
        </div>

        {nextAppointment ? (
          <>
            <div className="now-time">{formatTime(nextAppointment.time)}</div>
            <div className="now-client">{nextAppointment.clientName}</div>

            <div className="now-type">
              {getTypeIcon(nextAppointment.type)}
              <span>{getLabel(nextAppointment.type)}</span>
            </div>

            <div className="now-description">
              {nextAppointment.description || 'Keine Beschreibung'}
            </div>

            <div className="hero-actions">
              {nextAppointment.phone && (
                <a href={`tel:${nextAppointment.phone}`} title="Anrufen">
                  <FiPhone />
                </a>
              )}

              {phoneNumber && phoneNumber.length >= 11 && (
                <a
                  href={`https://wa.me/${phoneNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="WhatsApp"
                >
                  <FiMessageCircle />
                </a>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="now-empty-icon">
              <FiCheckCircle />
            </div>
            <div className="now-client small">Kein weiterer Termin heute</div>
            <div className="now-description">
              Alle Termine für heute sind erledigt oder es steht nichts mehr an.
            </div>
          </>
        )}
      </section>

      <section className="dayplan-stats">
        <div className="stat-card">
          <FiMessageSquare />
          <strong>{stats.consultations}</strong>
          <small>Beratungen</small>
        </div>

        <div className="stat-card">
          <FiPenTool />
          <strong>{stats.tattoo}</strong>
          <small>Stechtermine</small>
        </div>

        <div className="stat-card">
          <FiRefreshCw />
          <strong>{stats.touchup}</strong>
          <small>Nachstechen</small>
        </div>

        <div className="stat-card">
          <FiCalendar />
          <strong>{stats.total}</strong>
          <small>Heute</small>
        </div>
      </section>

      <section className="queue-section">
        <div className="queue-header">
          <h2>Today's Queue</h2>
          <span>{appointmentsToday.length} Termine</span>
        </div>

        {loading ? (
          <div className="queue-empty">Lade Tagesplan...</div>
        ) : appointmentsToday.length > 0 ? (
          <div className="queue-list">
            {appointmentsToday.map((appointment) => {
              const active = nextAppointment?.id === appointment.id;
              const past = isPast(appointment);
              const status = appointment.workflow_status || 'waiting';

              return (
                <div
                  key={appointment.id}
                  className={`queue-row ${appointment.type} ${status} ${active ? 'active' : ''} ${past ? 'past' : ''} ${appointment.invitation_state && appointment.invitation_state !== 'confirmed' ? 'consultation-awaiting' : ''}`}
                >
                  <button
                    type="button"
                    className={`queue-status ${status}`}
                    disabled={Boolean(appointment.invitation_state && appointment.invitation_state !== 'confirmed')}
                    onClick={() => toggleWorkflowStatus(appointment.id)}
                    title={status}
                  >
                    {getWorkflowIcon(status)}
                  </button>

                  <div className="queue-time">
                    {formatTime(appointment.time)}
                  </div>

                  <div className="queue-main">
                    <strong>{appointment.clientName}{appointment.invitation_state && appointment.invitation_state !== 'confirmed' ? ' · wartet auf Bestätigung' : ''}</strong>
                    <span>{appointment.description || 'Keine Beschreibung'}</span>
                  </div>

                  <div className="queue-meta">
                    <span>{getLabel(appointment.type)}</span>
                    <small>{appointment.artistName || 'Beratung'}</small>
                  </div>

                  <div className="queue-duration">
                    {appointment.duration ? `${appointment.duration}m` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="queue-empty">Heute keine Termine.</div>
        )}
      </section>
    </div>
  );
}

export default DayPlan;